import React from "react";

import type { Localizer } from "@/main/react/Types";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { DiscussionVersionSummary } from "@/main/common/DiscussionVersionTypes";
import DateFormatter from "@/main/common/DateFormatter";

/**
 * The author's own edit history for one published post — which version, and when.
 *
 * Slice 3 of `designs/01-now/2026-09-06-version-history-member-exposure-design.md`, shipped
 * **owner-only** as operator decision D5. "Public list" and "public diff" were both offered and
 * declined: `discussion_versions` rows carry every prior body, and an author who edited something out
 * did so deliberately.
 *
 * ## `canEdit` decides what is OFFERED. The server decides what is SEEN.
 *
 * `canEdit` reaches this component from the SSR dataset, so it is a claim about the page, not a
 * permission. The owner-only guarantee is the 403 out of
 * `DiscussionService.findDiscussionOwnedByUserOrThrow`, and a terminal fetch failure here renders the
 * generic error rather than anything version-shaped. Treating this prop as the boundary is the defect
 * §4.2 names.
 *
 * ## A closed disclosure that fetches once, on open
 *
 * Most owner views never ask this question and `GET /versions` is a real query, so nothing is
 * requested until the member presses the control. It does not re-fetch on a second open either — the
 * answer cannot change while they are standing on the page.
 *
 * ## What it may render
 *
 * Four states and no fifth: loading, terminal failure, "not edited", and the list. **A failed load and
 * an empty history must not produce the same answer**, which is why the failure branch renders its own
 * marked node instead of falling through to the empty state. Nothing here ever renders a row it did
 * not receive.
 *
 * Instrumentation: none, per the design's §6. Opening the disclosure is a control, counted by the UI
 * control census through its `data-census-id`; it emits no `UserActivityEventType`.
 */
export type DiscussionVersionHistoryProps = {
    localizer: Localizer;
    discussionPublicId: string;
    /**
     * Whether the viewer is the post's author, as the page believes it. Gates the CONTROL only — see
     * the note above on why this is not the security boundary.
     */
    canEdit: boolean;
};

type LoadState =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "failed" }
    | { kind: "loaded"; versions: DiscussionVersionSummary[] };

export function DiscussionVersionHistory({
    localizer,
    discussionPublicId,
    canEdit,
}: DiscussionVersionHistoryProps) {
    const [open, setOpen] = React.useState(false);
    const [state, setState] = React.useState<LoadState>({ kind: "idle" });
    const dateFormatter = React.useMemo(() => new DateFormatter(localizer), [localizer]);

    const openAndLoad = React.useCallback(() => {
        setOpen((wasOpen) => !wasOpen);
        setState((current) => {
            // Anything but `idle` means the question has already been asked once, and the answer
            // cannot change while the member is on this page. A `failed` state is deliberately NOT
            // retried on reopen: a silent re-request on every press is how a 403 becomes a loop.
            if (current.kind !== "idle") {
                return current;
            }
            void ClientFactory.getDiscussionVersions(discussionPublicId)
                .then((response) => {
                    setState({ kind: "loaded", versions: response?.data ?? [] });
                })
                .catch(() => {
                    setState({ kind: "failed" });
                });
            return { kind: "loading" };
        });
    }, [discussionPublicId]);

    if (!canEdit) {
        // Absent, not disabled. There is nothing to press and nothing that hints a history exists.
        return null;
    }

    const title = localizer("discussion.versionHistory.title");

    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={openAndLoad}
                aria-expanded={open}
                className="rounded-full px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                data-census-id="discussion-version-history-trigger"
                data-playwright-id="discussion-version-history-trigger"
            >
                {title}
            </button>

            {open ? (
                <div
                    className="mt-2 rounded-2xl border border-border p-3"
                    data-playwright-id="discussion-version-history"
                >
                    <h3 className="mb-2 text-xs font-medium text-foreground">{title}</h3>
                    {state.kind === "loading" ? (
                        <p className="text-xs text-muted-foreground">
                            {localizer("shared.dialog.loading")}
                        </p>
                    ) : null}
                    {state.kind === "failed" ? (
                        <p
                            className="text-xs text-muted-foreground"
                            role="alert"
                            data-playwright-id="discussion-version-history-error"
                        >
                            {localizer("typescript.discussion.server_error_generic")}
                        </p>
                    ) : null}
                    {state.kind === "loaded" && state.versions.length <= 1 ? (
                        // One version is the row every publish writes. "One version" and "never
                        // edited" are the same fact, and the member's vocabulary is the second one.
                        <p className="text-xs text-muted-foreground">
                            {localizer("discussion.versionHistory.empty")}
                        </p>
                    ) : null}
                    {state.kind === "loaded" && state.versions.length > 1 ? (
                        <ul className="space-y-1">
                            {state.versions.map((version, index) => (
                                <li
                                    key={version.versionNumber}
                                    className="flex items-baseline gap-2 text-xs text-muted-foreground"
                                    data-playwright-id="discussion-version-history-entry"
                                    data-version-number={version.versionNumber}
                                >
                                    <time dateTime={version.createdAt}>
                                        {localizer(
                                            "discussion.versionHistory.entry",
                                            dateFormatter.format(version.createdAt),
                                        )}
                                    </time>
                                    {/*
                                      * Badged by POSITION in a list the server returns newest-first,
                                      * which is the one thing that is true of "current" regardless of
                                      * how many versions exist or what they are numbered.
                                      */}
                                    {index === 0 ? (
                                        <span className="rounded-full bg-accent px-1.5 py-0.5 text-accent-foreground">
                                            {localizer("discussion.versionHistory.current")}
                                        </span>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export default DiscussionVersionHistory;
